import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import type { Note } from 'sr-types/lib/search/v1/graphql';
import type { EntityName, EntitySecurity } from '../auth/Security';
import { SECURITY_ACTIONS, useSecurity } from '../auth/Security';
import type { SecuredActionType } from '../components/ActionType';
import { TEMP_ID_PREFIX } from '../constants/constants';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';

type UseLexicalRendererActionsParams = {
    note?: Note;
    noteId: string;
    entityName?: EntityName;
    entitySecurity?: EntitySecurity;
    onSave?: (title: string, content: string) => Promise<unknown>;
    onEdit?: (note: Note) => void;
    onShare?: () => void;
    onDelete?: (id: string) => Promise<unknown>;
    onAssociateStatusNote?: {
        saveFunction?: (note: Note) => void;
        loadingState?: boolean;
        statusNote?: Note;
    };
    addPendingNoteAction?: (tempId: string, cb: (noteId: string) => void) => void;
    disableAllActions: boolean;
    enableMaxLineClamp: boolean;
    isEditing: boolean;
    expanded: boolean;
    setIsHover: (isHover: boolean) => void;
    setIsEditing: (isEditing: boolean) => void;
    setExpanded: Dispatch<SetStateAction<boolean>>;
    revertToContent: () => void;
};

export function useLexicalRendererActions({
    note,
    noteId,
    entityName,
    entitySecurity,
    onSave,
    onEdit,
    onShare,
    onDelete,
    onAssociateStatusNote,
    addPendingNoteAction,
    disableAllActions,
    enableMaxLineClamp,
    isEditing,
    expanded,
    setIsHover,
    setIsEditing,
    setExpanded,
    revertToContent
}: UseLexicalRendererActionsParams) {
    const t = useI18n();
    const editActionText = t('actions.edit');
    const shareActionText = t('notes.share');
    const deleteActionText = t('actions.delete');
    const statusNoteActionText = t('note.status');
    const collapseExpandActionText = expanded ? t('actions.collapse') : t('actions.expand');

    const saveStatusNoteFunction = onAssociateStatusNote?.saveFunction;
    const currentStatusNoteId =
        onAssociateStatusNote?.statusNote?.id || onAssociateStatusNote?.statusNote?.reference?.id;
    const isCurrentStatusNote = Boolean(noteId && currentStatusNoteId && noteId === currentStatusNoteId);

    const { allowed: canCommentOnParentEntity } = useSecurity(SECURITY_ACTIONS.COMMENT, {
        entityName,
        entitySecurity
    });

    const handleAddStatusNote = useCallback(() => {
        if (note?.id.startsWith(TEMP_ID_PREFIX)) {
            addPendingNoteAction?.(note?.id || note.reference?.id, (pendingNoteId) =>
                saveStatusNoteFunction({
                    ...note,
                    id: pendingNoteId,
                    reference: { id: pendingNoteId }
                })
            );
        } else {
            saveStatusNoteFunction(note);
        }
    }, [addPendingNoteAction, note, saveStatusNoteFunction]);

    const securedActions = useMemo<SecuredActionType[]>(() => {
        if (!entityName || !canCommentOnParentEntity) {
            return [];
        }

        const noteSecurityTarget = note
            ? {
                  entityName: ENTITY_CONSTANTS.note,
                  entitySecurity: getNoteEntitySecurity(note.id || noteId, note.security)
              }
            : undefined;
        // Notes: gate edit/share/delete on the note row. Summary/content: gate edit on the parent entity.
        const editSecurityTarget = noteSecurityTarget ?? { entityName, entitySecurity };
        const secured: SecuredActionType[] = [];

        // Status note: parent entity EDIT (outer menu still requires COMMENT).
        if (typeof saveStatusNoteFunction === 'function') {
            secured.push({
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName,
                entitySecurity,
                label: statusNoteActionText,
                icon: Icons.NoteAdd,
                onClick: handleAddStatusNote,
                loading: onAssociateStatusNote?.loadingState && note?.id === onAssociateStatusNote?.statusNote?.id,
                disabled: disableAllActions || onAssociateStatusNote?.loadingState || isCurrentStatusNote,
                dataTestId: 'icon-add-status-note'
            });
        }
        if (typeof onShare === 'function' && noteSecurityTarget) {
            secured.push({
                securityAction: SECURITY_ACTIONS.SHARE,
                ...noteSecurityTarget,
                label: shareActionText,
                icon: Icons.Share,
                disabled: disableAllActions,
                onClick: () => {
                    setIsHover(false);
                    revertToContent();
                    onShare();
                },
                dataTestId: 'icon-share-note'
            });
        }
        if (typeof onSave === 'function' || typeof onEdit === 'function') {
            secured.push({
                securityAction: SECURITY_ACTIONS.EDIT,
                ...editSecurityTarget,
                label: editActionText,
                icon: Icons.Edit,
                disabled: disableAllActions || isEditing,
                onClick: () => {
                    setIsHover(false);
                    revertToContent();
                    setIsEditing(true);
                    onEdit?.(note);
                },
                dataTestId: 'icon-edit-note'
            });
        }
        if (typeof onDelete === 'function' && noteSecurityTarget) {
            secured.push({
                securityAction: SECURITY_ACTIONS.DELETE,
                ...noteSecurityTarget,
                label: deleteActionText,
                icon: Icons.Delete,
                onClick: async () => await onDelete(noteId),
                dataTestId: 'icon-delete-note',
                disabled: disableAllActions
            });
        }
        // Collapse/expand: EDIT permission only (no onSave/onEdit required).
        if (enableMaxLineClamp) {
            secured.push({
                securityAction: SECURITY_ACTIONS.EDIT,
                ...editSecurityTarget,
                label: collapseExpandActionText,
                icon: expanded ? Icons.UnfoldLess : Icons.UnfoldMore,
                disabled: disableAllActions,
                onClick: () => setExpanded((prev) => !prev),
                dataTestId: 'icon-collapse-note'
            });
        }

        return secured;
    }, [
        entityName,
        canCommentOnParentEntity,
        entitySecurity,
        saveStatusNoteFunction,
        onShare,
        onSave,
        onEdit,
        onDelete,
        enableMaxLineClamp,
        statusNoteActionText,
        handleAddStatusNote,
        onAssociateStatusNote?.loadingState,
        onAssociateStatusNote?.statusNote?.id,
        note,
        disableAllActions,
        isCurrentStatusNote,
        shareActionText,
        editActionText,
        isEditing,
        deleteActionText,
        noteId,
        collapseExpandActionText,
        expanded,
        revertToContent,
        setIsHover,
        setIsEditing,
        setExpanded
    ]);

    const responsiveActionsProps = useMemo(() => ({ securedActions }), [securedActions]);

    return {
        responsiveActionsProps
    };
}

/** Fail-closed note row security for ABAC; missing/invalid security → no allowed actions. */
function getNoteEntitySecurity(noteId: string, security: Note['security']): EntitySecurity {
    if (!security || !Array.isArray(security.allowedActions)) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(`Note with id ${noteId} is missing security property, defaulting to no permissions`);
        }
        return { allowedActions: [] };
    }

    return { allowedActions: security.allowedActions };
}
